type PlanDays = 7 | 14 | 28;
type PackSize = 250 | 400 | 600 | 800;

const PACK_SIZES: PackSize[] = [250, 400, 600, 800];
const PLAN_DAYS: PlanDays[] = [7, 14, 28];

/**
 * Fallback: infer plan days from product title/handle when ID map is empty.
 * Returns null if no match found.
 */
function inferDaysFromProduct(product: any): PlanDays | null {
  const title = String(product.title ?? "").toLowerCase();
  const handle = String(product.handle ?? "").toLowerCase();
  const combined = `${title} ${handle}`;

  // Check for explicit day patterns
  for (const days of PLAN_DAYS) {
    if (combined.includes(`${days}-day`) || combined.includes(`${days} day`)) {
      return days;
    }
  }

  // Legacy fallback: "hidden-plans-product" defaults to 7-day
  if (handle === "hidden-plans-product" || handle.includes("hidden-plan")) {
    return 7;
  }

  return null;
}

export function buildHiddenPriceMap(
  hiddenPriceProducts: any[],
  hiddenPriceIdMap?: Record<string, string>
) {
  const out: Record<PlanDays, Partial<Record<PackSize, number>>> = {
    7: {},
    14: {},
    28: {},
  };

  // Build reverse map: productId → days
  const idToDays: Record<string, PlanDays> = {};
  const hasIdMap = hiddenPriceIdMap && Object.keys(hiddenPriceIdMap).length > 0;

  if (hasIdMap) {
    for (const [days, id] of Object.entries(hiddenPriceIdMap!)) {
      const daysNum = Number(days) as PlanDays;
      if (PLAN_DAYS.includes(daysNum)) {
        idToDays[id] = daysNum;
      }
    }
  }

  for (const product of hiddenPriceProducts ?? []) {
    // Primary: Use ID map for lookup
    let days = idToDays[String(product.id)];

    // Fallback: Use string matching if ID map is empty or doesn't have this product
    if (!days) {
      days = inferDaysFromProduct(product) ?? undefined as unknown as PlanDays;
      if (days) {
        console.log("[buildHiddenPriceMap] Using fallback string matching for product", product.id, product.title, "→", days, "days");
      }
    }

    if (!days) {
      console.warn("[buildHiddenPriceMap] No days mapping for product", product.id, product.title);
      continue;
    }

    for (const variant of product.variants ?? []) {
      const grams = Number(variant?.title);
      if (!PACK_SIZES.includes(grams as PackSize)) continue;

      const price = Number(variant?.price);
      if (!Number.isFinite(price)) continue;

      out[days][grams as PackSize] = price;
    }
  }

  return out;
}
